import React, { useState, useEffect } from 'react';
import { Plus, Book, Edit, Trash2, ChevronsLeft, ChevronLeft, ChevronRight, ChevronsRight } from 'lucide-react';
import { styles, Modal } from '../theme';

import { API_URL, fetchWithAuth } from '../api';
import { usePermissions } from '../context/PermissionContext';

const ChequeBooks = ({ token, postedPeriods }) => {
  const { canApprove, canCreate, canUpdate, canDelete, getModuleBranches } = usePermissions();
  const [chequeBooks, setChequeBooks] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [perPage, setPerPage] = useState(10);
  const [totalPages, setTotalPages] = useState(0);
  const [searchQuery, setSearchQuery] = useState('');
  const [branches, setBranches] = useState([]);
  const [bankDetails, setBankDetails] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState({ bank_detail_id: '', series_start: '', series_end: '', date_received: new Date().toISOString().split('T')[0] });
  const [filter, setFilter] = useState({
    branch_id: '',
    search: ''
  });

  useEffect(() => {
    fetchChequeBooks(currentPage, perPage, searchQuery, filter.branch_id);
    fetchBranches();
    fetchBankDetails();
  }, [currentPage, perPage, searchQuery, filter.branch_id]);

  const fetchChequeBooks = async (page = 1, limit = 10, search = '', branchId = '') => {
    try {
      const queryParams = new URLSearchParams({
        page,
        per_page: limit,
        search: search,
        branch_id: branchId
      });

      const res = await fetchWithAuth(`${API_URL}/cheque-books?${queryParams.toString()}`, { headers: { Authorization: `Bearer ${token}` } });
      const data = await res.json();

      setChequeBooks(data.books);
      setTotalCount(data.total_count);
      setTotalPages(data.total_pages);
    } catch (err) {
      console.error('Error fetching cheque books:', err);
    }
  };

  const fetchBranches = async () => {
    try {
      const res = await fetchWithAuth(`${API_URL}/branchesdropdown`, { headers: { Authorization: `Bearer ${token}` } });
      const data = await res.json();
      setBranches(data);
    } catch (err) {
      console.error('Error fetching branches:', err);
    }
  };

  const fetchBankDetails = async () => {
    try {
      const res = await fetchWithAuth(`${API_URL}/bank-details/all`, { headers: { Authorization: `Bearer ${token}` } });
      const data = await res.json();
      setBankDetails(data);
    } catch (err) {
      console.error('Error fetching bank details:', err);
    }
  };

  const getSelectedBranchId = () => {
    if (!form.bank_detail_id) return null;
    const selectedBank = bankDetails.find(b => String(b.id) === String(form.bank_detail_id));
    return selectedBank ? selectedBank.branch_id : null;
  };

  const isPeriodRestricted = (branchId, dateString, postedPeriods) => {
    if (!branchId || !dateString || !postedPeriods) return false;
    const date = new Date(dateString);
    const month = date.getMonth() + 1;
    const year = date.getFullYear();

    return postedPeriods.some(p =>
      p.branch_id === parseInt(branchId) &&
      p.month === month &&
      p.year === year
    );
  };

  const getMinDate = (branchId, postedPeriods) => {
    if (!branchId || !postedPeriods || postedPeriods.length === 0) return "";
    const branchPostings = postedPeriods.filter(
      (p) => parseInt(p.branch_id) === parseInt(branchId)
    );
    if (branchPostings.length === 0) return "";
    const latest = branchPostings.sort((a, b) => {
      if (a.year !== b.year) return b.year - a.year;
      return b.month - a.month;
    })[0];
    const nextMonth = latest.month === 12 ? 1 : latest.month + 1;
    const nextYear = latest.month === 12 ? latest.year + 1 : latest.year;
    return `${nextYear}-${String(nextMonth).padStart(2, "0")}-01`;
  };

  const handleSubmit = async () => {
    if (!form.bank_detail_id || !form.series_start || !form.series_end || !form.date_received) {
      alert('All fields are required');
      return;
    }

    if (isNaN(form.series_start) || isNaN(form.series_end)) {
      alert('Series numbers must be numerical');
      return;
    }

    if (parseInt(form.series_start) > parseInt(form.series_end)) {
      alert('Series start cannot be greater than series end');
      return;
    }

    const branchId = getSelectedBranchId();
    if (isPeriodRestricted(branchId, form.date_received, postedPeriods)) {
      alert('This period has been posted for the selected branch. No transactions can be created or updated.');
      return;
    }

    try {
      const url = editingId ? `${API_URL}/cheque-books/${editingId}` : `${API_URL}/cheque-books`;
      const method = editingId ? 'PUT' : 'POST';
      const res = await fetchWithAuth(url, {
        method,
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(form)
      });
      if (res.ok) {
        fetchChequeBooks();
        setShowForm(false);
        setEditingId(null);
        setForm({ bank_detail_id: '', series_start: '', series_end: '', date_received: new Date().toISOString().split('T')[0] });
      } else {
        const errData = await res.json();
        alert('Error: ' + (errData.error || JSON.stringify(errData)));
      }
    } catch (err) {
      console.error('Error saving cheque book:', err);
    }
  };

  const handleEdit = (cb) => {
    setForm({
      bank_detail_id: cb.bank_detail_id || '',
      series_start: cb.series_start || '',
      series_end: cb.series_end || '',
      date_received: cb.date_received ? new Date(cb.date_received).toISOString().split('T')[0] : ''
    });
    setEditingId(cb.id);
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this cheque book? This will also delete ALL unused/used cheques under this book!')) return;
    try {
      await fetchWithAuth(`${API_URL}/cheque-books/${id}`, { method: 'DELETE', headers: { Authorization: `Bearer ${token}` } });
      fetchChequeBooks();
    } catch (err) {
      console.error('Error deleting cheque book:', err);
    }
  };

  return (
    <div>
      <div style={styles.header}>
        <h1 style={styles.title}>Cheque Books</h1>
        <button onClick={() => { setShowForm(true); setEditingId(null); setForm({ bank_detail_id: '', series_start: '', series_end: '', date_received: new Date().toISOString().split('T')[0] }); }} style={styles.button}>
          <Plus style={{ width: '20px', height: '20px' }} /> Add Cheque Book
        </button>
      </div>

      <Modal
        isOpen={showForm}
        onClose={() => setShowForm(false)}
        title={`${editingId ? 'Edit' : 'Add'} Cheque Book`}
        maxWidth="600px"
      >
        <div style={styles.grid2}>
          <div style={styles.formGroup}>
            <label style={styles.label}>Bank Detail *</label>
            <select value={form.bank_detail_id} onChange={(e) => setForm({ ...form, bank_detail_id: e.target.value })} style={styles.input}>
              <option value="">Select Bank Account</option>
              {bankDetails.map(b => <option key={b.id} value={b.id}>{b.bank_account_number} - {b.bank_account_name} ({b.branch_name})</option>)}
            </select>
          </div>
          <div style={styles.formGroup}>
            <label style={styles.label}>Series Start *</label>
            <input
              type="text"
              value={form.series_start}
              onChange={(e) => {
                const val = e.target.value.replace(/\D/g, '');
                setForm({ ...form, series_start: val });
              }}
              style={styles.input}
              placeholder="0001"
            />
          </div>
          <div style={styles.formGroup}>
            <label style={styles.label}>Series End *</label>
            <input
              type="text"
              value={form.series_end}
              onChange={(e) => {
                const val = e.target.value.replace(/\D/g, '');
                setForm({ ...form, series_end: val });
              }}
              style={styles.input}
              placeholder="0050"
            />
          </div>
          <div style={styles.formGroup}>
            <label style={styles.label}>Date Received *</label>
            {(() => {
              const branchId = getSelectedBranchId();
              return (
                <input
                  type="date"
                  min={getMinDate(branchId, postedPeriods)}
                  value={form.date_received}
                  onChange={(e) => setForm({ ...form, date_received: e.target.value })}
                  style={{
                    ...styles.input,
                    borderColor: isPeriodRestricted(branchId, form.date_received, postedPeriods) ? '#ef4444' : '#d1d5db'
                  }}
                />
              );
            })()}
          </div>
        </div>
        <div style={{ marginTop: '24px', display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
          <button onClick={() => setShowForm(false)} style={{ ...styles.button, ...styles.buttonSecondary }}>Cancel</button>
          <button onClick={handleSubmit} style={styles.button}>Save</button>
        </div>
      </Modal>

      {/* Filters */}
      <div style={{
        ...styles.card,
        marginBottom: '20px',
        display: 'grid',
        gridTemplateColumns: '1fr 1fr',
        gap: '16px'
      }}>
        <div style={styles.formGroup}>
          <label style={styles.label}>Filter by Branch</label>
          <select
            value={filter.branch_id}
            onChange={(e) => { setFilter({ ...filter, branch_id: e.target.value }); setCurrentPage(1); }}
            style={styles.input}
          >
            <option value="">All Branches</option>
            {branches.map(b => (
              <option key={b.id} value={b.id}>{b.name}</option>
            ))}
          </select>
        </div>
        <div style={styles.formGroup}>
          <label style={styles.label}>Search</label>
          <input
            type="text"
            placeholder="Search by account number or name..."
            value={searchQuery}
            onChange={(e) => { setSearchQuery(e.target.value); setCurrentPage(1); }}
            style={styles.input}
          />
        </div>
      </div>

      <div style={{ ...styles.card, padding: 0, overflow: 'hidden' }}>
        <table style={styles.table}>
          <thead>
            <tr>
              <th style={styles.th}>Bank Account</th>
              <th style={styles.th}>Series Start</th>
              <th style={styles.th}>Series End</th>
              <th style={styles.th}>Date Received</th>
              <th style={{ ...styles.th, textAlign: 'right' }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {chequeBooks.map(cb => (
              <tr key={cb.id}>
                <td style={styles.td}>
                  <div style={{ fontSize: '13px', fontWeight: '500' }}>{cb.bank_account_number} - {cb.bank_account_name}</div>
                  <div style={{ fontSize: '11px', color: '#6b7280' }}>{cb.branch_name}</div>
                </td>
                <td style={styles.td}>{cb.series_start}</td>
                <td style={styles.td}>{cb.series_end}</td>
                <td style={styles.td}>{cb.date_received ? new Date(cb.date_received).toLocaleDateString() : '-'}</td>
                <td style={{ ...styles.td, textAlign: 'right' }}>
                  {/* {canUpdate('cheque_books') && (
                    <button onClick={() => handleEdit(cb)} style={{ ...styles.iconButton, color: '#4f46e5', marginRight: '8px' }}>
                      <Edit size={18} />
                    </button>
                  )} */}
                  {canDelete('cheque_books') && (
                    <button onClick={() => handleDelete(cb.id)} style={{ ...styles.iconButton, color: '#ef4444' }}>
                      <Trash2 size={18} />
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Pagination Controls */}
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginTop: '24px',
        padding: '16px',
        backgroundColor: 'white',
        borderRadius: '8px',
        boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
      }}>
        <div style={{ fontSize: '14px', color: '#6b7280' }}>
          Showing {totalCount === 0 ? 0 : Math.min((currentPage - 1) * perPage + 1, totalCount)} to {Math.min(currentPage * perPage, totalCount)} of {totalCount} entries
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '4px', marginRight: '16px' }}>
            <span style={{ fontSize: '14px', color: '#6b7280' }}>Rows per page:</span>
            <select
              value={perPage}
              onChange={(e) => {
                setPerPage(parseInt(e.target.value));
                setCurrentPage(1);
              }}
              style={{
                padding: '4px 8px',
                borderRadius: '4px',
                border: '1px solid #d1d5db',
                fontSize: '14px'
              }}
            >
              {[5, 10, 25, 50, 100].map(size => (
                <option key={size} value={size}>{size}</option>
              ))}
            </select>
          </div>
          <div style={{ display: 'flex', gap: '4px' }}>
            <button
              onClick={() => setCurrentPage(1)}
              disabled={currentPage === 1}
              style={{
                ...styles.iconButton,
                padding: '6px',
                opacity: currentPage === 1 ? 0.5 : 1,
                cursor: currentPage === 1 ? 'not-allowed' : 'pointer',
                border: '1px solid #d1d5db'
              }}
            >
              <ChevronsLeft size={16} />
            </button>
            <button
              onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
              disabled={currentPage === 1}
              style={{
                ...styles.iconButton,
                padding: '6px',
                opacity: currentPage === 1 ? 0.5 : 1,
                cursor: currentPage === 1 ? 'not-allowed' : 'pointer',
                border: '1px solid #d1d5db'
              }}
            >
              <ChevronLeft size={16} />
            </button>

            <div style={{ display: 'flex', gap: '4px', margin: '0 8px' }}>
              {[...Array(totalPages || 0)].map((_, i) => {
                const pageNum = i + 1;
                if (
                  pageNum === 1 ||
                  pageNum === totalPages ||
                  (pageNum >= currentPage - 1 && pageNum <= currentPage + 1)
                ) {
                  return (
                    <button
                      key={pageNum}
                      onClick={() => setCurrentPage(pageNum)}
                      style={{
                        padding: '6px 12px',
                        borderRadius: '4px',
                        border: '1px solid #d1d5db',
                        backgroundColor: currentPage === pageNum ? '#4f46e5' : 'white',
                        color: currentPage === pageNum ? 'white' : '#374151',
                        fontSize: '14px',
                        cursor: 'pointer'
                      }}
                    >
                      {pageNum}
                    </button>
                  );
                } else if (
                  (pageNum === 2 && currentPage > 3) ||
                  (pageNum === totalPages - 1 && currentPage < totalPages - 2)
                ) {
                  return <span key={pageNum} style={{ padding: '6px' }}>...</span>;
                }
                return null;
              })}
            </div>

            <button
              onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
              disabled={currentPage === totalPages || totalPages === 0}
              style={{
                ...styles.iconButton,
                padding: '6px',
                opacity: (currentPage === totalPages || totalPages === 0) ? 0.5 : 1,
                cursor: (currentPage === totalPages || totalPages === 0) ? 'not-allowed' : 'pointer',
                border: '1px solid #d1d5db'
              }}
            >
              <ChevronRight size={16} />
            </button>
            <button
              onClick={() => setCurrentPage(totalPages)}
              disabled={currentPage === totalPages || totalPages === 0}
              style={{
                ...styles.iconButton,
                padding: '6px',
                opacity: (currentPage === totalPages || totalPages === 0) ? 0.5 : 1,
                cursor: (currentPage === totalPages || totalPages === 0) ? 'not-allowed' : 'pointer',
                border: '1px solid #d1d5db'
              }}
            >
              <ChevronsRight size={16} />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ChequeBooks;
